import * as React from 'react'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import { strings } from '../../lib/localization'
import { getArticleDetails, navigateToCategory, trackFeedbackVote, navigateToArticle } from '../../actions/answers'
import { Header } from './header'
import { Article } from './article'
import { MainMenu } from '../mainmenu/menu'
import { FeedbackAlternatives } from './feedbackalternatives'
import { currentParamsSelector } from '../../selectors/navigation'
import { userIdFromAnswersHubSelector, formIdFromAnswersHubSelector } from '../../selectors/hub'
import {
  currentAnswersArticleFromParamsDetailsSelector,
  currentAnswersArticlesDetailsFromParamsStatusSelector
 } from '../../selectors/articleDetails'
import { scrollTo } from '../../lib/utils'
import { Container } from '../container'
import { RootState } from '../../types/rootstate'

import '../../sass/answers/details.scss'
import { ArticleDetailsBreadcrumbs } from './breadcrumbs/article-details-breadcrumbs'

interface StateProps {
  params: any
  status: RootState['articleDetails']['key']['status']
  article: RootState['articleDetails']['key']['article'],
  userId: number
  formId: number
}

interface DispatchProps {
  getArticleDetails: (userId: number, answersId: number, categoryId: number) => void
  trackFeedbackVote: (userId: number, answersId: number, articleId: number, vote: 'yes' | 'no') => void
  navigateToArticle: (userId: number, answersId: number, articleId: number) => void
}

interface OwnProps {}

type DetailsProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState): StateProps {
  return {
    params: currentParamsSelector(state),
    status: currentAnswersArticlesDetailsFromParamsStatusSelector(state),
    article: currentAnswersArticleFromParamsDetailsSelector(state) as RootState['articleDetails']['key']['article'],
    userId: userIdFromAnswersHubSelector(state),
    formId: formIdFromAnswersHubSelector(state)
  }
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    getArticleDetails: (userId, answersId, articleId) =>
      dispatch(getArticleDetails(userId, answersId, articleId)),
    trackFeedbackVote: (userId, answersId, articleId, vote) =>
      dispatch(trackFeedbackVote(userId, answersId, articleId, vote)),
    navigateToArticle: (userId, answersId, articleId) =>
      dispatch(navigateToArticle(userId, answersId, articleId))
  }
}

interface DetailsState {
  showVoteNoFeedback: boolean
  showVoteYesFeedback: boolean
}

class DetailsComponent extends React.Component<DetailsProps, DetailsState> {
  references: {
    scrollContainer: HTMLDivElement,
    voteFeedbackContainer: HTMLDivElement
  } = {
    scrollContainer: null,
    voteFeedbackContainer: null
  }
  detailsBody = React.createRef<HTMLDivElement>()

  constructor (props) {
    super(props)

    this.state = {
      showVoteYesFeedback: false,
      showVoteNoFeedback: false
    }
  }

  componentDidMount () {
    if (this.props.article.id !== this.props.params.articleId) {
      this.props.getArticleDetails(
        this.props.params.userId,
        this.props.params.answersId,
        this.props.params.articleId
      )
    }
    this.injectArticleLinks()
  }

  componentWillUnmount () {
    this.removeInjectedArticleLinks()
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevProps.params.articleId !== this.props.params.articleId) {
      this.props.getArticleDetails(
        this.props.params.userId,
        this.props.params.answersId,
        this.props.params.articleId
      )
    }
    this.removeInjectedArticleLinks()
    this.injectArticleLinks()
  }

  doVoteYes = () => {
    this.props.trackFeedbackVote(
      this.props.params.userId,
      this.props.params.answersId,
      this.props.params.articleId,
      'yes'
    )
    this.setState({
      showVoteYesFeedback: true,
      showVoteNoFeedback: false
    })
  }

  handleVoteYesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    this.doVoteYes()
  }

  handleVoteYesKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.doVoteYes()
    }
  }

  doVoteNo = () => {
    this.props.trackFeedbackVote(
      this.props.params.userId,
      this.props.params.answersId,
      this.props.params.articleId,
      'no'
    )
    this.setState({
      showVoteNoFeedback: true,
      showVoteYesFeedback: false
    })
  }

  handleVoteNoClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    this.doVoteNo()
  }

  handleVoteNoKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.doVoteNo()
    }
  }

  handleFeedbackAlternativesLoaded = () => {
    if (this.references.scrollContainer && this.references.voteFeedbackContainer) {
      const containerOffsetTop = this.references.voteFeedbackContainer.offsetTop

      scrollTo(this.references.scrollContainer, containerOffsetTop, 200)
    }
  }

  injectArticleLinks () {
    if (this.detailsBody.current) {
      const articleLinks = this.detailsBody.current.querySelectorAll('[data-articleid]')
      for (let i = 0; i < articleLinks.length; i++) {
        articleLinks[i].addEventListener('click', this.handleArticleLinkClick)
        articleLinks[i].addEventListener('keydown', this.handleArticleLinkKeyDown)
      }
    }
  }

  removeInjectedArticleLinks () {
    if (this.detailsBody.current) {
      const articleLinks = this.detailsBody.current.querySelectorAll('[data-articleid]')
      for (let i = 0; i < articleLinks.length; i++) {
        articleLinks[i].removeEventListener('click', this.handleArticleLinkClick)
        articleLinks[i].removeEventListener('keydown', this.handleArticleLinkKeyDown)
      }
    }
  }

  handleArticleLinkClick = (e: MouseEvent) => {
    e.preventDefault()
    const target = e.target as HTMLElement
    const userId = this.props.userId
    const formId = this.props.formId
    const articleId = Number(target.getAttribute('data-articleid'))
    this.props.navigateToArticle(userId, formId, articleId)
  }

  handleArticleLinkKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const target = e.target as HTMLElement
      const userId = this.props.userId
      const formId = this.props.formId
      const articleId = Number(target.getAttribute('data-articleid'))
      this.props.navigateToArticle(userId, formId, articleId)
    }
  }

  render () {
    const body = {
      __html: this.props.article.qBod
    }
    const shouldShowFeedback = this.props.article.help === '1' && !this.state.showVoteYesFeedback && this.props.status === 'success'
    const shouldShowFeedbackNo = this.state.showVoteNoFeedback

    return (
      <Container name='answers'>
        <Header />
        <div className={'answers-dynamic-frame ' + (shouldShowFeedback && 'hasRating')}>

        <div
          ref={(node) => this.references.scrollContainer = node}
          className='answers-details container'
        >
          <div className='row answers-content-wrapper'>
            {this.props.status === 'fetching' &&
              <div className='loadingSpinner'>
                <div className='accentPrimaryButton imbox-loader'>
                  <div></div>
                </div>
              </div>}

            <ArticleDetailsBreadcrumbs article={this.props.article} answersId={this.props.params.answersId} />

            {this.props.status === 'failed' && <div className='col-xs-12 answers-content'>
              <div className='row'>
                <div className='col-xs-12'>
                  <div className='answers-subject'>{strings.answersDetailsFetchFailedHeader}</div>
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <div className='answers-body'>
                    {strings.answersDetailsFetchFailedContent}
                  </div>
                </div>
              </div>
            </div>}

            {this.props.status === 'not_found' && <div className='col-xs-12 answers-content'>
              <div className='row'>
                <div className='col-xs-12'>
                  <div className='answers-subject'>{strings.answersDetailsNotFoundHeader}</div>
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <div className='answers-body'>
                    {strings.answersDetailsNotFoundContent}
                  </div>
                </div>
              </div>
            </div>}

            {this.props.status === 'success' && <div className='col-xs-12 answers-content'>
              <div className='row'>
                <div className='col-xs-12'>
                  <div className='answers-subject'>{this.props.article.qSub}</div>
                </div>
              </div>

              {this.props.article.agentPicture !== null &&
              <div className='row'>
                <div className='col-xs-12'>

                  <div className='answers-author'>
                    <div className='answers-author-avatar'>
                      <img src={this.props.article.agentPicture} alt='' />
                    </div>

                    <div className='answers-meta-info'>
                      <span className='answers-meta-info-category'>{strings.answersDetailsCategory} {this.props.article.qCat}</span>
                      <span className='answers-meta-info-author'>{strings.answersDetailsAuthor} {this.props.article.agentName}</span>
                    </div>
                  </div>

                </div>
              </div>}

              <div
                ref={this.detailsBody}
                className='answers-body'
                dangerouslySetInnerHTML={body}>
              </div>

            </div>}
          </div>

          {shouldShowFeedback &&
          <div
            className='voteForm'
            ref={(node) => this.references.voteFeedbackContainer = node}
          >
            <div className='voteQuestion'>{this.props.article.helpText}</div>

            <div className='voteOptions'>
              <div className='voteOption voteUp'>
                <div
                  className={'button btn-outline-primary accentSecondaryButton ' + (this.state.showVoteYesFeedback ? 'active' : '')}
                  onClick={this.handleVoteYesClick}
                  role='button'
                  tabIndex={0}
                  onKeyDown={this.handleVoteYesKeyDown}
                >
                  <span>{this.props.article.helpYes}</span>
                </div>
              </div>

              <div
                className='voteOption voteDown'
                onClick={this.handleVoteNoClick}
                tabIndex={0}
                role='button'
                onKeyDown={this.handleVoteNoKeyDown}
              >
                <div className={'button btn-outline-primary accentSecondaryButton ' + (this.state.showVoteNoFeedback ? 'active' : '')}>
                  <span>{this.props.article.helpNo}</span>
                </div>
              </div>
            </div>
          </div>
          }

          {this.state.showVoteYesFeedback &&
          <div className='voteForm'>
            <div className='voteThankYou'>
              {strings.answersVoteThankYou}
            </div>
          </div>
          }

          {shouldShowFeedbackNo && <FeedbackAlternatives hasLoaded={this.handleFeedbackAlternativesLoaded} />}
        </div>

        </div>
      </Container>
    )
  }
}

const Details = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(DetailsComponent)


export {
  Details
}
